import type { ArchiveItem } from '@arrow/game-engine';
import type { PuzzleType } from '@arrow/puzzle-schema';
import type { MouseEvent } from 'react';

import { Icon, SeasonIcon } from '../../components/icons.tsx';
import { Nib } from '../../components/Nib.tsx';
import { itemLabel, plural } from './model.ts';

/**
 * A special collection's night poster (docs/mockups/specials-card.html, option 1, and
 * docs/mockups/halloween-header.html, B): its Play tile, and the top of its page. Both draw
 * the same scene: stars, a moon, a hill, the season's marks and Nib in his seasonal hat.
 */

const PLAY_TRIANGLE = (
	<svg viewBox="0 0 12 12" aria-hidden="true" className="sp-tri">
		<path d="M2 1l9 5-9 5z" />
	</svg>
);

/** Where the five stars sit, as a share of the scene (decorative). */
const STARS: ReadonlyArray<readonly [number, number]> = [
	[8, 14],
	[33, 8],
	[55, 24],
	[68, 10],
	[44, 40],
];

/** The night scene behind a special's poster. Decorative only. */
export function NightScene({ type }: { type: PuzzleType }) {
	return (
		<span className="night-scene" aria-hidden="true">
			{STARS.map(([x, y]) => (
				<i key={`${x}-${y}`} className="ns-star" style={{ left: `${x}%`, top: `${y}%` }} />
			))}
			<span className="ns-moon" />
			<span className="ns-hill" />
			<span className="ns-marks">
				<SeasonIcon type={type} />
				<SeasonIcon type={type} />
			</span>
			<Nib pose="happy" type={type} className="ns-nib" />
		</span>
	);
}

/** "3 puzzles · Free", "1 of 3 solved", "3 of 3 · all perfect": where you are with a pack. */
export function specialStatus(items: readonly ArchiveItem[]): string {
	const n = items.length;
	const done = items.filter((it) => it.status === 'done');
	if (done.length === 0) return `${n} ${plural(n, 'puzzle')} · Free`;
	if (done.length < n) return `${done.length} of ${n} solved`;
	return done.every((it) => it.perfect) ? `${n} of ${n} · all perfect` : `${n} of ${n} solved`;
}

/** The kicker over a special's title: its mark and "Special". */
function Kicker({ type }: { type: PuzzleType }) {
	return (
		<span className="sp-kick">
			<SeasonIcon type={type} />
			Special
		</span>
	);
}

/**
 * A special's tile on Play: the night poster, small. No sales line and no level pips (the
 * pack is the thing): its name, where you are with it, and Play (or Solved once all are).
 * The whole tile opens the collection, as every tile does.
 */
export function SpecialTile({
	id,
	type,
	name,
	items,
	top,
	href,
	onOpen,
	onPlay,
}: {
	id: string;
	type: PuzzleType;
	name: string;
	items: readonly ArchiveItem[];
	top: ArchiveItem | null;
	href: string;
	onOpen: () => void;
	onPlay: () => void;
}) {
	const open = (e: MouseEvent<HTMLAnchorElement>) => {
		if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
		e.preventDefault();
		onOpen();
	};
	const status = specialStatus(items);
	return (
		<li className="stk special on-night" data-type={type}>
			<NightScene type={type} />
			<Kicker type={type} />
			<h3 className="stk-name">
				<a id={id} href={href} onClick={open} aria-label={`${name}, special, ${status}: open the collection`}>
					{name}
				</a>
			</h3>
			<p className="sp-status" aria-hidden="true">
				{status}
			</p>
			{top ? (
				<button type="button" className="sp-play" onClick={onPlay} aria-label={`Play the top one in ${name}: ${itemLabel(top)}`}>
					{PLAY_TRIANGLE}
					Play
				</button>
			) : (
				<span className="sp-play sp-solved" aria-hidden="true">
					Solved
					<Icon.check />
				</span>
			)}
		</li>
	);
}

/**
 * The top of a special's page: the poster. On a phone it runs up behind the back pill and
 * the menu (the masthead goes `.on-night` with it), edge to edge; beside the rail it's a card.
 */
export function SpecialPoster({ type, name, sub, items, heading }: { type: PuzzleType; name: string; sub: string; items: readonly ArchiveItem[]; heading: 'h1' | 'h2' }) {
	const H = heading;
	return (
		<div className={`special-poster on-night${heading === 'h1' ? ' full' : ''}`} data-type={type}>
			<NightScene type={type} />
			<div className="sp-text">
				<Kicker type={type} />
				<H className="sp-title stack-title" id="stack-title" tabIndex={-1}>
					{name}
				</H>
				<p className="sp-sub">{sub}</p>
			</div>
			<p className="sp-chip">{specialStatus(items)}</p>
		</div>
	);
}
